import React, { useRef, useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import emailjs from '@emailjs/browser';
import { BlinkingCursor } from '../../utils/CommonStyles';
import { Bio } from '../../data/constants';

// Animation keyframes
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 0%; }
  100% { background-position: 0% 0%; }
`;

const blinkEffect = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const typingEffect = keyframes`
  from { width: 0 }
  to { width: 100% }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(122, 0, 255, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(122, 0, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(122, 0, 255, 0); }
`;

const cursorBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 80px 0px 120px 0px;
  background: linear-gradient(
    343deg,
    rgba(10, 13, 19, 1) 0%,
    rgba(19, 22, 30, 1) 50%,
    rgba(10, 13, 19, 1) 100%
  );

  @media (max-width: 960px) {
    padding: 60px 0px 80px 0px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1350px;
  padding: 0px 20px;
  gap: 12px;
`;

const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 52px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.text_primary};
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
  }

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;

    &:after {
      width: 120px;
    }
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 25px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TerminalWindow = styled.div`
  width: 100%;
  max-width: 1100px;
  margin-top: 20px;
  border-radius: 10px;
  background-color: #1a1a1a;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  overflow: visible;
  border: 1px solid #333;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;

  &:after {
    content: none;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(to bottom, transparent 0%, rgba(0, 30, 60, 0.05) 100%),
      repeating-linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.1) 0px,
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px,
        transparent 2px
      );
    pointer-events: none;
    opacity: 0.3;
    z-index: 0;
    border: none;
    border-radius: 10px;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(to bottom, transparent, rgba(120, 170, 255, 0.15), transparent);
  animation: ${scanEffect} 5s linear infinite;
  z-index: 10;
  pointer-events: none;
`;

const TerminalHeader = styled.div`
  display: flex;
  align-items: center;
  background-color: #262626;
  padding: 12px 20px;
  border-bottom: 1px solid #333;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
  grid-column: 1 / -1;
`;

const TerminalDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => props.color};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2);
    box-shadow: 0 0 5px ${props => props.color};
  }
`;

const TerminalTitle = styled.div`
  flex: 1;
  text-align: center;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ddd;
  letter-spacing: 1px;
`;

const LeftPane = styled.div`
  padding: 30px;
  position: relative;
  z-index: 2;
  background: rgba(30, 30, 35, 0.4);

  @media (max-width: 960px) {
    grid-row: 2;
    min-height: 300px;
  }
`;

// const EarthContainer = styled.div`
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   z-index: 1;
//   opacity: 0.8;

//   &:after {
//     content: '';
//     position: absolute;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     background: radial-gradient(
//       circle at center,
//       transparent 30%,
//       rgba(26, 26, 26, 0.7) 70%,
//       rgba(26, 26, 26, 0.95) 100%
//     );
//     pointer-events: none;
//   }
// `;

const RightPane = styled.div`
  padding: 30px;
  border-left: 1px solid #333;
  background-color: rgba(35, 35, 40, 0.6);

  @media (max-width: 960px) {
    border-left: none;
    border-top: 1px solid #333;
  }
`;

const TerminalLine = styled.div`
  margin: 10px 0;
  line-height: 1.6;
  color: #fff;
  display: flex;
  position: relative;
  z-index: 3;
  font-weight: 500;

  &:before {
    content: '$ ';
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
    margin-right: 10px;
  }
`;

const TypingText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
  letter-spacing: 0.15em;
  animation: ${typingEffect} 2s steps(30, end);
  font-size: 16px;
`;

const CommandOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;
  width: 100%;
  animation: ${fadeIn} 0.5s ease-out;
  z-index: 3;
  color: #e0e0e0;
  font-size: 15px;
  line-height: 1.7;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }
`;

const ContactForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  gap: 14px;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: ${props => (props.hasError ? '24px' : '10px')};
`;

const InputLabel = styled.div`
  position: absolute;
  left: 15px;
  top: -10px;
  background: #1a1a1a;
  padding: 0 5px;
  font-size: 14px;
  color: ${({ theme }) => theme.primary};
  font-family: 'Courier New', monospace;
`;

const ErrorMessage = styled.div`
  color: #ff4757;
  font-size: 12px;
  margin-top: 5px;
  font-family: 'Courier New', monospace;
  display: flex;
  align-items: center;

  &:before {
    content: '!';
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    background-color: #ff4757;
    color: #1a1a1a;
    border-radius: 50%;
    margin-right: 6px;
    font-weight: bold;
  }
`;

const ContactInput = styled.input`
  width: 100%;
  background-color: transparent;
  border: 1px solid ${props => (props.hasError ? '#ff4757' : props.theme.text_secondary + '70')};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 6px;
  padding: 12px 16px;
  font-family: 'Courier New', monospace;
  transition: all 0.3s ease;

  &:focus {
    border: 1px solid ${props => (props.hasError ? '#ff4757' : props.theme.primary)};
    box-shadow: 0 0 8px
      ${props => (props.hasError ? 'rgba(255, 71, 87, 0.3)' : 'rgba(122, 0, 255, 0.3)')};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + '50'};
  }
`;

const ContactInputMessage = styled.textarea`
  width: 100%;
  background-color: transparent;
  border: 1px solid ${props => (props.hasError ? '#ff4757' : props.theme.text_secondary + '70')};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 6px;
  padding: 12px 16px;
  font-family: 'Courier New', monospace;
  min-height: 140px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    border: 1px solid ${props => (props.hasError ? '#ff4757' : props.theme.primary)};
    box-shadow: 0 0 8px
      ${props => (props.hasError ? 'rgba(255, 71, 87, 0.3)' : 'rgba(122, 0, 255, 0.3)')};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + '50'};
  }
`;

const ContactButton = styled.button`
  background: rgba(122, 0, 255, 0.2);
  border: 1px solid ${({ theme }) => theme.primary + '80'};
  color: ${({ theme }) => theme.text_primary};
  font-family: 'Courier New', monospace;
  padding: 12px 20px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 5px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: rgba(122, 0, 255, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  ${props =>
    props.sending &&
    css`
      pointer-events: none;
      opacity: 0.8;
      animation: ${pulse} 1.5s infinite;
    `}
`;

const CursorBlink = styled.span`
  display: inline-block;
  width: 8px;
  height: 16px;
  background-color: ${({ theme }) => theme.primary};
  margin-left: 5px;
  animation: ${cursorBlink} 1s infinite;
`;

const Status = styled.div`
  font-family: 'Courier New', monospace;
  color: ${props => (props.success ? '#4CAF50' : '#F44336')};
  margin-top: 5px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Contact = () => {
  const form = useRef();
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ show: false, success: false, message: '' });
  const [typedCommands, setTypedCommands] = useState([]);

  // Form validation state
  const [formValues, setFormValues] = useState({
    from_email: '',
    from_name: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({
    from_email: '',
    from_name: '',
    subject: '',
    message: '',
  });

  // Email configuration
  const TO_EMAIL = 'nquocdung.04@gmail.com';

  // EmailJS configuration
  const EMAILJS_SERVICE_ID = 'service_053ouos';
  const EMAILJS_TEMPLATE_ID = 'template_q2oqhn4';
  const EMAILJS_PUBLIC_KEY = 'pK10MW6MKD4lYzl5t';

  // Input change handler
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  // Validation functions
  const validateEmail = email => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      from_email: '',
      from_name: '',
      subject: '',
      message: '',
    };

    // Email validation
    if (!formValues.from_email.trim()) {
      newErrors.from_email = 'Email is required';
      valid = false;
    } else if (!validateEmail(formValues.from_email)) {
      newErrors.from_email = 'Please enter a valid email address';
      valid = false;
    }

    // Name validation
    if (!formValues.from_name.trim()) {
      newErrors.from_name = 'Name is required';
      valid = false;
    } else if (formValues.from_name.length < 2) {
      newErrors.from_name = 'Name must be at least 2 characters';
      valid = false;
    } else if (formValues.from_name.length > 50) {
      newErrors.from_name = 'Name cannot exceed 50 characters';
      valid = false;
    }

    // Subject validation
    if (!formValues.subject.trim()) {
      newErrors.subject = 'Subject is required';
      valid = false;
    } else if (formValues.subject.length < 3) {
      newErrors.subject = 'Subject must be at least 3 characters';
      valid = false;
    } else if (formValues.subject.length > 100) {
      newErrors.subject = 'Subject cannot exceed 100 characters';
      valid = false;
    }

    // Message validation
    if (!formValues.message.trim()) {
      newErrors.message = 'Message is required';
      valid = false;
    } else if (formValues.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
      valid = false;
    } else if (formValues.message.length > 1000) {
      newErrors.message = 'Message cannot exceed 1000 characters';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // Simulating the typing effect of terminal commands
  useEffect(() => {
    const commandsToType = ['init contact_protocol.sh'];

    const typeCommands = async () => {
      for (const cmd of commandsToType) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setTypedCommands(prev => [...prev, cmd]);
      }
    };

    typeCommands();
  }, []);

  const handleSubmit = e => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Stop form submission if validation fails
    }

    setSending(true);
    setStatus({ show: false, success: false, message: '' });

    const templateParams = {
      to_email: TO_EMAIL,
    };

    emailjs
      .sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, form.current, EMAILJS_PUBLIC_KEY, {
        templateParams,
      })
      .then(
        result => {
          setSending(false);
          setStatus({
            show: true,
            success: true,
            message: 'Message transmitted successfully!',
          });
          if (form.current) {
            form.current.reset();
            // Also reset form values in state
            setFormValues({
              from_email: '',
              from_name: '',
              subject: '',
              message: '',
            });
          }
          console.log('Email sent successfully', result.text);
        },
        error => {
          setSending(false);
          setStatus({
            show: true,
            success: false,
            message: 'Transmission failed. Please try again.',
          });
          console.error('Email sending failed:', error.text);
        }
      );
  };

  return (
    <Container id="Contact">
      <Wrapper>
        <TitleSection>
          <Title>Contact</Title>
          <Desc>Feel free to reach out for any questions or opportunities!</Desc>
        </TitleSection>

        <TerminalWindow>
          <ScanLine />
          <TerminalHeader>
            <TerminalDot color="#ff5f56" />
            <TerminalDot color="#ffbd2e" />
            <TerminalDot color="#27c93f" />
            <TerminalTitle>
              {Bio.name.toLowerCase().replaceAll(' ', '')}@backend-dev ~ /contact
            </TerminalTitle>
          </TerminalHeader>

          <LeftPane>
            {/* <EarthContainer>
              <EarthCanvas />
            </EarthContainer> */}

            {typedCommands.length > 0 && (
              <>
                <TerminalLine>
                  <TypingText>{typedCommands[0]}</TypingText>
                </TerminalLine>
                <CommandOutput>
                  Initializing secure connection... <br />
                  Setting up communication channels... <br />
                  Ready to receive your message.
                </CommandOutput>
              </>
            )}

            <TerminalLine>
              <BlinkingCursor>_</BlinkingCursor>
            </TerminalLine>
          </LeftPane>

          <RightPane>
            <div
              style={{ fontFamily: 'Courier New, monospace', color: '#ddd', marginBottom: '20px' }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'white',
                  marginBottom: '5px',
                }}
              >
                #!/usr/bin/env contact_form
              </div>
              Establish connection with {Bio.name}:
            </div>

            <ContactForm ref={form} onSubmit={handleSubmit}>
              {/* Hidden field for recipient email (my email) */}
              <input type="hidden" name="to_email" value={TO_EMAIL} />

              <InputGroup hasError={!!errors.from_email}>
                <InputLabel>from:</InputLabel>
                <ContactInput
                  placeholder="your@email.com"
                  name="from_email"
                  required
                  value={formValues.from_email}
                  onChange={handleInputChange}
                  hasError={!!errors.from_email}
                />
                {errors.from_email && <ErrorMessage>{errors.from_email}</ErrorMessage>}
              </InputGroup>

              <InputGroup hasError={!!errors.from_name}>
                <InputLabel>name:</InputLabel>
                <ContactInput
                  placeholder="Your Name"
                  name="from_name"
                  required
                  value={formValues.from_name}
                  onChange={handleInputChange}
                  hasError={!!errors.from_name}
                />
                {errors.from_name && <ErrorMessage>{errors.from_name}</ErrorMessage>}
              </InputGroup>

              <InputGroup hasError={!!errors.subject}>
                <InputLabel>subject:</InputLabel>
                <ContactInput
                  placeholder="Subject"
                  name="subject"
                  required
                  value={formValues.subject}
                  onChange={handleInputChange}
                  hasError={!!errors.subject}
                />
                {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
              </InputGroup>

              <InputGroup hasError={!!errors.message}>
                <InputLabel>message:</InputLabel>
                <ContactInputMessage
                  placeholder="Type your message here..."
                  name="message"
                  rows={6}
                  required
                  value={formValues.message}
                  onChange={handleInputChange}
                  hasError={!!errors.message}
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </InputGroup>

              <ContactButton type="submit" sending={sending}>
                {sending ? (
                  <>
                    Transmitting
                    <CursorBlink />
                  </>
                ) : (
                  <>Send Message</>
                )}
              </ContactButton>

              {status.show && (
                <Status success={status.success}>
                  {status.success ? '✓' : '✕'} {status.message}
                </Status>
              )}
            </ContactForm>

            <div
              style={{
                fontFamily: 'Courier New, monospace',
                color: '#888',
                fontSize: '12px',
                marginTop: '20px',
              }}
            >
              $ echo "All messages are encrypted and will be delivered directly to {Bio.name}'s
              inbox."
            </div>
          </RightPane>
        </TerminalWindow>
      </Wrapper>
    </Container>
  );
};

export default Contact;
